import { computeDimensions } from "@/components/assessment/assessmentScoring";

export const RESPONSE_CHANGE_OPTIONS = [
  { value: 1, label: "No change", detail: "Nothing meaningful changed in the target response." },
  { value: 2, label: "Minimal change", detail: "A small change was noticeable." },
  { value: 3, label: "Noticeable change", detail: "A clear change was noticeable." },
  { value: 4, label: "Large change", detail: "The target response changed substantially." },
  { value: 5, label: "Significant change", detail: "The target response changed very substantially." },
];

// Compatibility alias for older imports while the UI moves away from the old
// Adaptive Score terminology. The options no longer carry emoji or point value.
export const ADAPTIVE_RESPONSE_OPTIONS = RESPONSE_CHANGE_OPTIONS;

const recordTime = (entry) => {
  const value = entry?.completedAt || entry?.createdAt;
  const time = new Date(value || 0).getTime();
  return Number.isFinite(time) ? time : 0;
};

const localDayKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const responseMagnitude = (entry) =>
  Number(entry?.responseMagnitude ?? entry?.changeMagnitude ?? entry?.improvementRating) || 0;

export function changeResponseLabel(value) {
  return RESPONSE_CHANGE_OPTIONS.find((option) => option.value === Number(value))?.label || "Not rated";
}

export const adaptiveResponseLabel = changeResponseLabel;

const patternKey = (entry) =>
  [
    entry?.reportedStates?.[0] || entry?.system || "state",
    entry?.recommendation?.activityKey || entry?.recommendation?.title || "input",
  ].join("::");

const patternTitle = (entry) =>
  entry?.recommendation?.title || entry?.recommendation?.activityKey || "Physiological input";

export function buildResponsePatterns(entries = []) {
  const grouped = new Map();
  [...entries]
    .filter((entry) => entry?.completed)
    .sort((a, b) => recordTime(b) - recordTime(a))
    .forEach((entry) => {
      const key = patternKey(entry);
      const current = grouped.get(key) || {
        key,
        title: patternTitle(entry),
        state: entry?.reportedStates?.[0] || entry?.system || "Comparable state",
        count: 0,
        significantOrLarge: 0,
        noChange: 0,
        worse: 0,
        latestAt: entry.completedAt || entry.createdAt || null,
      };
      current.count += 1;
      const magnitude = responseMagnitude(entry);
      if (entry.feltWorse || entry.response === "worse") current.worse += 1;
      else if (magnitude >= 4) current.significantOrLarge += 1;
      else if (magnitude === 1 || entry.response === "same") current.noChange += 1;
      grouped.set(key, current);
    });

  return [...grouped.values()]
    .filter((pattern) => pattern.count >= 2)
    .map((pattern) => {
      let classification = "context-dependent";
      if (pattern.worse / pattern.count >= 0.5) classification = "needs-adjustment";
      else if (pattern.significantOrLarge / pattern.count >= 0.6) classification = "larger-change-pattern";
      else if (pattern.noChange / pattern.count >= 0.6) classification = "often-little-change";
      return { ...pattern, classification };
    })
    .sort((a, b) => b.count - a.count || recordTime(b) - recordTime(a));
}

export function computeProgressModel({ assessment, entries = [] }) {
  const baseline = computeDimensions(assessment?.answers || {});
  const baselineTime = new Date(assessment?.completedAt || 0).getTime();
  const currentCycleEntries = entries.filter((entry) => {
    const created = new Date(entry?.createdAt || 0).getTime();
    const sameBaseline =
      !entry?.baselineCompletedAt ||
      Number(entry.baselineCompletedAt) === Number(assessment?.completedAt);
    return Number.isFinite(created) && (!Number.isFinite(baselineTime) || created >= baselineTime) && sameBaseline;
  });
  const completed = currentCycleEntries.filter((entry) => entry.completed);
  const observations = completed.filter(
    (entry) =>
      entry.feltWorse ||
      entry.response === "worse" ||
      responseMagnitude(entry) > 0 ||
      entry.response === "same" ||
      entry.response === "better",
  );
  const daysReturned = new Set(
    currentCycleEntries.map((entry) => localDayKey(entry.createdAt)).filter(Boolean),
  ).size;
  const activitiesExplored = new Set(
    completed
      .map((entry) => entry.recommendation?.activityKey || entry.recommendation?.title)
      .filter(Boolean),
  ).size;
  const patterns = buildResponsePatterns(currentCycleEntries);
  const responseCounts = RESPONSE_CHANGE_OPTIONS.map((option) => ({
    ...option,
    count: observations.filter(
      (entry) => !entry.feltWorse && entry.response !== "worse" && responseMagnitude(entry) === option.value,
    ).length,
  }));
  const worseCount = observations.filter(
    (entry) => entry.feltWorse || entry.response === "worse",
  ).length;

  return {
    modelVersion: "participation-learning-v1",
    baseline,
    currentCycleEntries,
    completed,
    observations,
    daysReturned,
    activitiesExplored,
    patterns,
    responseCounts,
    worseCount,
    observationsCount: observations.length,
    completedCount: completed.length,
    patternsIdentified: patterns.length,
    checkInCount: currentCycleEntries.length,
  };
}

// Kept temporarily so a stale cached component cannot crash during the PWA
// update. It intentionally returns the learning model and no dynamic score,
// missed-day regression, or physiological-improvement points.
export const computeAdaptiveScore = computeProgressModel;
